/**
 * Action category LOGIC — resolve/sort/group/partition the categorized actions
 * that drive the row dropdown, right-click menu, and mobile sheet.
 *
 * Split out of `action-categories.ts` (organization only — no behavior change).
 * Static data lives in `./data.ts`.
 */

import type { ActionId } from "../types/action-names";
import {
  ACTION_CATEGORY_BY_ID,
  ACTION_ORDER_BY_ID,
  CATEGORY_LABEL,
  COLLAPSED_CATEGORIES,
  RANK,
  type ActionCategory,
} from "./data";

/** Minimal shape needed to categorize — works for any DetailAction. */
interface CategorizableAction {
  id: string;
  category?: ActionCategory;
}

/** Resolve an action's category: explicit field → id map → `create` fallback. */
export function getActionCategory(action: CategorizableAction): ActionCategory {
  if (action.category) return action.category;
  return ACTION_CATEGORY_BY_ID[action.id as ActionId] ?? "create";
}

/** Resolve an action's intra-category order: explicit field → id map → 0. */
export function getActionOrder(action: CategorizableAction & { order?: number }): number {
  if (typeof action.order === "number") return action.order;
  return ACTION_ORDER_BY_ID[action.id as ActionId] ?? 0;
}

/**
 * Stable sort by (category rank, intra-category order, original index). Equal
 * items keep their authored order via the index tiebreak.
 */
export function sortActionsByCategory<T extends CategorizableAction & { order?: number }>(
  actions: T[],
): T[] {
  return actions
    .map((action, index) => ({ action, index }))
    .sort((a, b) => {
      const rankDiff =
        RANK[getActionCategory(a.action)] - RANK[getActionCategory(b.action)];
      if (rankDiff !== 0) return rankDiff;
      const orderDiff = getActionOrder(a.action) - getActionOrder(b.action);
      if (orderDiff !== 0) return orderDiff;
      return a.index - b.index;
    })
    .map(({ action }) => action);
}

/**
 * Group already-categorizable items into ordered category sections. Items are
 * sorted first, then bucketed; empty categories are omitted. Used by the
 * context menu / dropdown / sheet to render dividers + section labels.
 */
export function groupActionsByCategory<T extends CategorizableAction & { order?: number }>(
  actions: T[],
): Array<{ category: ActionCategory; label: string; items: T[] }> {
  const sorted = sortActionsByCategory(actions);
  const groups: Array<{ category: ActionCategory; label: string; items: T[] }> = [];
  for (const action of sorted) {
    const category = getActionCategory(action);
    const last = groups[groups.length - 1];
    if (last && last.category === category) {
      last.items.push(action);
    } else {
      groups.push({ category, label: CATEGORY_LABEL[category], items: [action] });
    }
  }
  return groups;
}

/* ------------------------------------------------------------------ *
 * Collapse — fold the low-priority tail into a "More…" group so a long
 * action list stays short by default. The top categories render inline;
 * `integration` + `admin` collapse behind a "More" submenu/disclosure;
 * `destructive` is pinned LAST and always visible (Delete must stay reachable).
 * ------------------------------------------------------------------ */

/**
 * Collapse only pays off on long menus. If the whole list is short, OR there
 * are fewer than 2 collapsible actions, inline everything — a "More…" wrapper
 * around 0-1 hidden items is pure friction.
 */
const INLINE_ALL_MAX = 8; // ≤ this many total actions → never collapse
const MIN_COLLAPSIBLE = 2; // need at least this many integration/admin items to fold

/** Decide whether the collapsed "More…" group should be used at all. */
function shouldCollapse(total: number, collapsibleCount: number): boolean {
  return collapsibleCount >= MIN_COLLAPSIBLE && total > INLINE_ALL_MAX;
}

type Categorized = { category: ActionCategory };

/**
 * Split items into three render zones:
 *  - `top`    — inline (primary…navigate, plus integration/admin when NOT collapsing)
 *  - `more`   — integration/admin folded behind a "More…" submenu/disclosure
 *               (empty unless the menu is long enough to warrant collapsing)
 *  - `danger` — destructive, pinned last, always inline
 * Order within each zone is preserved (caller should pass sorted items).
 */
export function partitionByCategory<I extends Categorized>(
  items: I[],
): { top: I[]; more: I[]; danger: I[] } {
  const danger = items.filter((i) => i.category === "destructive");
  const nonDanger = items.filter((i) => i.category !== "destructive");
  const collapsible = nonDanger.filter((i) => COLLAPSED_CATEGORIES.has(i.category));

  if (!shouldCollapse(items.length, collapsible.length)) {
    // Short menu — keep integration/admin inline (already correctly positioned
    // between navigate and destructive by the category sort).
    return { top: nonDanger, more: [], danger };
  }

  return {
    top: nonDanger.filter((i) => !COLLAPSED_CATEGORIES.has(i.category)),
    more: collapsible,
    danger,
  };
}

/**
 * Group form of {@link partitionByCategory} for surfaces that render labeled
 * sections (the right-click context menu). Returns category groups per zone.
 */
export function partitionActionGroups<T extends CategorizableAction & { order?: number }>(
  actions: T[],
): {
  top: Array<{ category: ActionCategory; label: string; items: T[] }>;
  more: Array<{ category: ActionCategory; label: string; items: T[] }>;
  danger: Array<{ category: ActionCategory; label: string; items: T[] }>;
} {
  const groups = groupActionsByCategory(actions);
  const danger = groups.filter((g) => g.category === "destructive");
  const nonDanger = groups.filter((g) => g.category !== "destructive");
  const collapsibleGroups = nonDanger.filter((g) => COLLAPSED_CATEGORIES.has(g.category));

  // Threshold is measured in ACTIONS, not groups.
  const total = actions.length;
  const collapsibleCount = collapsibleGroups.reduce((n, g) => n + g.items.length, 0);

  if (!shouldCollapse(total, collapsibleCount)) {
    return { top: nonDanger, more: [], danger };
  }

  return {
    top: nonDanger.filter((g) => !COLLAPSED_CATEGORIES.has(g.category)),
    more: collapsibleGroups,
    danger,
  };
}
